/**
 * Read offline snapshots written by useOfflineCache / offlineSync.cache*.
 * Supports legacy bare JSON arrays and wrapped `{ data, cached_at }`.
 */

import { safeStorageGet, safeStorageSet } from '@/lib/storage';

export const OFFLINE_CACHE_KEYS = {
  tasks: 'offline-cache-tasks',
  shifts: 'offline-cache-shifts',
  myShifts: 'offline-cache-my-shifts',
  checklists: 'offline-cache-checklists',
  checklistInstances: 'offline-cache-checklist-instances',
  locations: 'offline-cache-locations',
} as const;

export type OfflineCacheSnapshot<T> = {
  data: T[];
  cachedAt: string | null;
};

type WrappedPayload<T> = {
  data?: T[];
  cached_at?: string;
};

function parseStorageArray<T>(raw: string | null): OfflineCacheSnapshot<T> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as T[] | WrappedPayload<T>;
    if (Array.isArray(parsed)) {
      return { data: parsed, cachedAt: null };
    }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.data)) {
      const cachedAt =
        typeof parsed.cached_at === 'string' && parsed.cached_at.trim()
          ? parsed.cached_at
          : null;
      return { data: parsed.data, cachedAt };
    }
  } catch {
    return null;
  }
  return null;
}

function readSnapshot<T>(key: string): OfflineCacheSnapshot<T> | null {
  if (typeof window === 'undefined') return null;
  return parseStorageArray<T>(safeStorageGet(key));
}

export function readOfflineTasks(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.tasks);
}

export function readOfflineShifts(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.shifts);
}

export function readOfflineMyShifts(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.myShifts);
}

export function readOfflineChecklists(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.checklists);
}

export function readOfflineChecklistInstances(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.checklistInstances);
}

export function readOfflineLocations(): OfflineCacheSnapshot<unknown> | null {
  return readSnapshot(OFFLINE_CACHE_KEYS.locations);
}

export function readOfflineShiftById(id: number): { shift: unknown; cachedAt: string | null } | null {
  const my = readOfflineMyShifts();
  const fromMy = my?.data.find((s) => (s as { id?: number })?.id === id);
  if (fromMy) return { shift: fromMy, cachedAt: my?.cachedAt ?? null };

  const all = readOfflineShifts();
  const fromAll = all?.data.find((s) => (s as { id?: number })?.id === id);
  if (fromAll) return { shift: fromAll, cachedAt: all?.cachedAt ?? null };

  return null;
}

export function readOfflinePendingChecklists(): {
  checklists: unknown[];
  scheduled_instances: unknown[];
  cachedAt: string | null;
} | null {
  const checklists = readOfflineChecklists();
  const instances = readOfflineChecklistInstances();
  if (!checklists?.data.length && !instances?.data.length) {
    return null;
  }
  const cachedAt = checklists?.cachedAt ?? instances?.cachedAt ?? null;
  return {
    checklists: checklists?.data ?? [],
    scheduled_instances: instances?.data ?? [],
    cachedAt,
  };
}

/** Latest cached_at across task/shift/checklist snapshots (for stale banner). */
export function getOfflineCacheFreshnessLabel(): string | null {
  const timestamps = [
    readOfflineTasks()?.cachedAt,
    readOfflineMyShifts()?.cachedAt,
    readOfflineShifts()?.cachedAt,
    readOfflineChecklists()?.cachedAt,
    readOfflineChecklistInstances()?.cachedAt,
  ].filter((t): t is string => Boolean(t));

  if (timestamps.length === 0) return null;

  const sorted = timestamps.sort();
  const latest = sorted[sorted.length - 1]!;
  try {
    return new Date(latest).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return latest;
  }
}

export function writeOfflineCachePayload(key: string, data: unknown[]): void {
  if (typeof window === 'undefined') return;
  safeStorageSet(
    key,
    JSON.stringify({
      data,
      cached_at: new Date().toISOString(),
    })
  );
}
